/**
 * Tab behavior for the Dose Clinical Studies section (WAI-ARIA Tabs pattern).
 *
 * Studies are theme blocks, so the section can't wrap them in server-rendered
 * tab markup — this element builds the product (SKU) tab bar from each
 * panel's data-tab-label when two or more studies exist. Each study's marker
 * tablist is server-rendered; both tab layers share the same delegated
 * activation and keyboard handling.
 *
 * Selecting a tab slides the SKU bar's background indicator (CSS transform
 * driven by --dose-clinical-studies-active-index) and swaps panels in two
 * phases: the outgoing panel fades out, then the incoming one fades in.
 */
class DoseClinicalStudies extends HTMLElement {
  static FADE_OUT_MS = 150;

  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.setupSkuTabs();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  setupSkuTabs() {
    const panels = [...this.querySelectorAll('[data-study-panel]')];
    if (panels.length < 2 || this.querySelector('.dose-clinical-studies__sku-tabs')) return;

    const sectionId = this.dataset.sectionId || 'dose-clinical-studies';
    const tablist = document.createElement('div');
    tablist.className = 'dose-clinical-studies__sku-tabs';
    tablist.setAttribute('role', 'tablist');
    tablist.setAttribute('aria-label', 'Products');
    tablist.style.setProperty('--dose-clinical-studies-tab-count', panels.length);
    tablist.style.setProperty('--dose-clinical-studies-active-index', 0);

    panels.forEach((panel, index) => {
      const panelId = `${sectionId}-study-${index + 1}-panel`;
      const tabId = `${sectionId}-study-${index + 1}-tab`;
      const selected = index === 0;

      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.hidden = !selected;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'dose-clinical-studies__sku-tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.textContent = this.formatTabLabel(panel.dataset.tabLabel || `Product ${index + 1}`);
      tablist.append(tab);
    });

    const indicator = document.createElement('span');
    indicator.className = 'dose-clinical-studies__sku-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    tablist.prepend(indicator);

    this.prepend(tablist);
    this.classList.add('dose-clinical-studies--tabbed');
  }

  /**
   * Glue everything after the first two words with non-breaking spaces so a
   * wrapping label breaks after the lead-in and keeps the product name on one
   * line ("DOSE FOR / YOUR LIVER", never "DOSE FOR YOUR / LIVER").
   */
  formatTabLabel(label) {
    const words = label.split(' ');
    if (words.length <= 2) return label;
    return `${words[0]} ${words[1]} ${words.slice(2).join(' ')}`;
  }

  handleClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    this.activateTab(tab);
  };

  handleKeydown = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;

    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(tab);
    let next = null;

    if (event.key === 'ArrowRight') {
      next = tabs[(index + 1) % tabs.length];
    } else if (event.key === 'ArrowLeft') {
      next = tabs[(index - 1 + tabs.length) % tabs.length];
    } else if (event.key === 'Home') {
      next = tabs[0];
    } else if (event.key === 'End') {
      next = tabs[tabs.length - 1];
    }

    if (!next) return;

    event.preventDefault();
    next.focus();
    this.activateTab(next);
  };

  handleBlockSelect = (event) => {
    const panel = event.target.closest?.('[data-study-panel]');
    if (!panel || !this.contains(panel)) return;

    const tab = this.querySelector(`[role="tab"][aria-controls="${panel.id}"]`);
    if (tab) this.activateTab(tab);
  };

  activateTab(tab) {
    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;

    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    let outgoing = null;
    let incoming = null;

    tabs.forEach((other, index) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = document.getElementById(other.getAttribute('aria-controls'));

      if (panel) {
        if (!panel.hidden) outgoing = panel;
        if (selected) incoming = panel;
      }

      if (selected) {
        tablist.style.setProperty('--dose-clinical-studies-active-index', index);
      }
    });

    if (incoming !== outgoing) {
      this.swapPanels(tablist, outgoing, incoming);
    }
  }

  /* Two-phase swap: outgoing fades out, then incoming fades in. Rapid
     re-clicks cancel the pending phase and settle instantly. */
  swapPanels(host, outgoing, incoming) {
    clearTimeout(host.swapTimer);

    if (this.reducedMotionQuery.matches || !outgoing) {
      if (outgoing) outgoing.hidden = true;
      if (incoming) {
        incoming.classList.remove('is-fading');
        incoming.hidden = false;
      }
      return;
    }

    outgoing.classList.add('is-fading');

    host.swapTimer = setTimeout(() => {
      outgoing.hidden = true;
      outgoing.classList.remove('is-fading');

      if (!incoming) return;

      incoming.classList.add('is-fading');
      incoming.hidden = false;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          incoming.classList.remove('is-fading');
        });
      });
    }, DoseClinicalStudies.FADE_OUT_MS);
  }
}

if (!customElements.get('dose-clinical-studies')) {
  customElements.define('dose-clinical-studies', DoseClinicalStudies);
}
